<template>
  <div>
    <div class="estore-body">
      <div class="estore-content inventory-content">
        <PageToolBar />
        <DataTable />
        <Pagination />
      </div>
    </div>
    <NewProduct
      :status="modal.new_product.status"
      :basic_details="basic_details"
      :simple_product="simple_product"
      :product_variables="product_variables"
      :variation_items="variation_items"
      :payment="payment_type"
      :ad_ons="ad_ons"
      :ad_on_items="ad_on_items"
    />

    <EditProduct
      :status="modal.edit_product.status"
      :basic_details="basic_details"
      :simple_product="simple_product"
      :product_variables="product_variables"
      :variation_items="variation_items"
      :payment="payment_type"
      :ad_ons="ad_ons"
      :ad_on_items="ad_on_items"
    />

    <ProductUpload :status="modal.croppa.status" :values="product_img_upload" />

    <ConfirmDelete
      :status="modal.delete_product.status"
      :name="modal.delete_product.name"
    />

    <ConfirmDeleteMultipleProducts
      :status="modal.delete_multiple_products.status"
      :product_ids="modal.delete_multiple_products.product_ids"
      :merchant_uuid="modal.delete_multiple_products.merchant_uuid"
    />

    <CheckTaggedProduct
      :status="modal.check_tagged_product.status"
      :name="modal.check_tagged_product.name"
    />
  </div>
</template>

<script>
import { mapState } from "vuex";
import notification from "../../services/notification";
import PageToolBar from "@/components/products/PageToolbar";
import DataTable from "@/components/products/DataTable";
import Pagination from "@/components/products/Pagination";
import NewProduct from "@/components/products/NewProduct";
import EditProduct from "@/components/products/EditProduct";
import ProductUpload from "@/components/products/ProductCroppa";
import ConfirmDelete from "@/components/products/ConfirmDelete";
import ConfirmDeleteMultipleProducts from "@/components/products/ConfirmDeleteMultipleProducts";
import CheckTaggedProduct from "@/components/products/CheckTaggedProduct";
import pvg from "@/services/product.variance.generator";

function initialBasicDetails() {
  return {
    name: "",
    description: "",
    product_variation: "simple",
    category_id: null,
    branch_uuids: [],
    selected_all_brands_branches: false,
  };
}

function initialSimpleProduct() {
  return {
    on_sale: false,
    visibility: true,
    stock: 1,
    currency: "PHP",
    regular_price: "1",
    sku: null,
    discount_type: "%",
    discount_value: "1",
    on_sale_from: "YYYY-MM-DD HH:MM:SS",
    on_sale_to: "YYYY-MM-DD HH:MM:SS",
    options: {
      currency: [{ value: "PHP", text: "PHP" }],
      discount: [
        { value: "%", text: "%" },
        { value: "PHP", text: "PHP" },
      ],
    },
    date_range: null,
  };
}

function initialProductVariables() {
  return {
    attributes: [
      {
        name: "",
        action: "create",
        variations: [{ name: "", action: "create" }],
      },
    ],
    discount: {
      discount_type: "%",
      discount_value: 1,
    },
    sale: {
      all: false,
      date_range: null,
    },
  };
}

function initialAdOns() {
  return {
    attributes: [
      {
        name: "",
        action: "create",
        limit: 1,
        variations: [{ name: "", action: "create", is_free: 0 }],
      },
    ],
    discount: {
      discount_type: "%",
      discount_value: 1,
    },
    sale: {
      all: false,
      date_range: null,
    },
  };
}

export default {
  name: "Inventory",
  components: {
    PageToolBar,
    DataTable,
    Pagination,
    NewProduct,
    EditProduct,
    ProductUpload,
    ConfirmDelete,
    ConfirmDeleteMultipleProducts,
    CheckTaggedProduct,
  },
  computed: {
    ...mapState("estoreInventories", ["modal", "payment_type", "product_data"]),
    ...mapState("estoreSettings", ["settings"]),
  },
  data() {
    return {
      product_img_upload: {
        croppa: {},
        sliderVal: 0,
        sliderMin: 0,
        sliderMax: 0,
      },
      basic_details: initialBasicDetails(),
      simple_product: initialSimpleProduct(),
      product_variables: initialProductVariables(),
      variation_items: [],
      variation_names: [],
      ad_ons: initialAdOns(),
      ad_on_items: [],
      ad_on_names: [],
    };
  },
  created: function () {
    this.getPaymentType();
  },
  methods: {
    formatDate: function (date) {
      return this.$moment(date).format("YYYY-MM-DD");
    },
    isMoment: function (date) {
      return this.$moment.isMoment(date);
    },
    addAttribute: function (attribute) {
      this.product_variables.attributes.push(attribute);
    },
    addVariation: function (index) {
      this.product_variables.attributes[index].variations.push({
        action: "create",
        name: "",
      });
    },
    deleteAttribute: function (index) {
      this.product_variables.attributes.splice(index, 1);
    },
    deleteVariation: function (param) {
      this.product_variables.attributes[param.attribute].variations.splice(
        param.variation,
        1
      );
    },
    generateVariations: function (origin) {
      let known_variants = new Set();
      let variations = this.product_variables["attributes"]
        .map((product) => {
          return product.variations;
        })
        .map((subarray) =>
          subarray.filter(
            (item) =>
              !known_variants.has(item.name.trim().toLowerCase()) &&
              known_variants.add(item.name.trim().toLowerCase())
          )
        );

      let param_variations = [];

      const timestamp = new Date().getUTCMilliseconds();

      let empty_attributes = {
        attribute_count: 0,
        variation_count: 0,
      };

      for (let index = 0; index < variations.length; index++) {
        this.product_variables["attributes"][index].variations =
          variations[index];
        let attribute = this.product_variables["attributes"][index];

        if (attribute.name == "") {
          empty_attributes.attribute_count++;
        }
        if (attribute.variations.length == 0) {
          empty_attributes.variation_count++;
        }

        this.product_variables["attributes"][index].variations =
          attribute.variations
            .map((variation, counter) => {
              const formattedVariations = {};
              formattedVariations["action"] = variation.action;
              formattedVariations["hash"] =
                variation.name
                  .slice(0, 4)
                  .toUpperCase()
                  .trim()
                  .replace(/ \D/g, "") +
                "-" +
                index +
                counter +
                timestamp;
              formattedVariations["name"] = variation.name.trim();
              return formattedVariations;
            })
            .filter((variation) => variation.name !== "");

        param_variations.push(
          attribute.variations.map((variation) => {
            return variation.name;
          })
        );
      }

      /* Start: Gather product_data decoded_variation_code with uuid for comparison in Edit Product */
      const old_variations =
        Object.keys(this.product_data).length > 0
          ? this.product_data.inventory.map((inventory) => {
              return {
                uuid: inventory.uuid,
                variations: [
                  inventory.decoded_variation_code
                    .map((variant) => variant.value)
                    .join(" | "),
                ],
                codes: inventory.variation_code.split("_"),
              };
            })
          : [];

      const exisiting_variations = {}; // to gather all existing attributes variations to get hash for new pairing
      if (origin == "edit_product") {
        this.product_data["attributes"]
          .map((attr) => attr.variations.map((variation) => variation.hash))
          .flat()
          .map((code) => {
            const tracking = code.split("-");
            exisiting_variations[tracking[0]] = tracking.pop();
          });
      }

      const collect_new_variations = {}; // gather all new attributes variations to collect hash for new pairing
      this.product_variables["attributes"]
        .map((attr) => attr.variations.map((variation) => variation))
        .flat()
        .filter((variation) => variation.action == "create")
        .map((variation) => variation.hash)
        .map((code) => {
          const tracking = code.split("-");
          collect_new_variations[tracking[0]] = tracking.pop();
        });
      /* End: Gather product_data decoded_variation_code with uuid for comparison in Edit Product */

      let combinations = pvg.combineArrays(param_variations);

      console.log(combinations);

      this.variation_names = combinations.map((variant) => {
        return {
          variation: variant.substring(1).replace(/_/g, " | ").trim(),
        };
      });

      let collect_items = [];

      this.variation_items = combinations.map((variant, index) => {
        let variation = variant.substring(1).replace(/_/g, " | ").trim();

        let var_code = variation
          .split("|")
          .map((code, counter) => {
            const transformedCode = [];
            const tracking = code.trim().slice(0, 4).toUpperCase();

            transformedCode.push(
              Object.keys(exisiting_variations).includes(tracking)
                ? `${tracking}-${exisiting_variations[tracking]}`
                : Object.keys(collect_new_variations).includes(tracking)
                ? `${tracking}-${collect_new_variations[tracking]}`
                : `${tracking}-${counter}${index}${timestamp}`
            );

            return transformedCode;
          })
          .join("_");

        collect_items = this.variation_items.map((item) => {
          return {
            action: item.action,
            // code:
            //     item.variation_code !== null
            //         ? item.variation_code
            //               .split("_")
            //               .map(code => {
            //                   return code.split("-")[0];
            //               })
            //               .join("_")
            //         : item.variation_code,
            variation: variation,
            variation_code: item.variation_code,
            stock: item.stock,
            currency: item.currency,
            regular_price: item.regular_price,
            sku: item.sku,
            on_sale: item.on_sale,
            discount_type: item.discount_type,
            discount_value: item.discount_value,
            on_sale_from: new Date(),
            on_sale_to: new Date(),
            date_range: item.date_range,
            visibility: item.visibility,
            uuid: item.uuid,
            is_limited_stock: item.is_limited_stock,
          };
        });

        let data_item = collect_items
          .map((item) => item)
          .filter((item) => item.code == var_code);

        let isData = data_item.length > 0;

        /* Start: Watch out for old variations with uuid to reassign values after generation of Variable Product */
        let old_variant_uuid = null;

        const is_old_variant = old_variations
          .map((item) => item)
          .map((item) => item.variations)
          .flat()
          .includes(variation);

        if (is_old_variant) {
          old_variant_uuid = old_variations
            .map((item) => item)
            .find((item) => item.variations.includes(variation)).uuid;
        }
        /* Start: Watch out for old variations with uuid to reassign values after generation of Variable Product */

        return {
          action: is_old_variant
            ? collect_items.find((item) => item.uuid == old_variant_uuid).action
            : "create",
          variation: variation,
          variation_code:
            isData || is_old_variant
              ? collect_items.find((item) => item.uuid == old_variant_uuid)
                  .variation_code
              : var_code,
          stock:
            isData || is_old_variant
              ? collect_items.find((item) => item.uuid == old_variant_uuid)
                  .stock
              : 1,
          currency: "PHP",
          regular_price:
            isData || is_old_variant
              ? collect_items.find((item) => item.uuid == old_variant_uuid)
                  .regular_price
              : 1,
          sku:
            isData || is_old_variant
              ? collect_items.find((item) => item.uuid == old_variant_uuid).sku
              : null,
          on_sale:
            isData || is_old_variant
              ? collect_items.find((item) => item.uuid == old_variant_uuid)
                  .on_sale
              : false,
          discount_type:
            isData || is_old_variant
              ? collect_items.find((item) => item.uuid == old_variant_uuid)
                  .discount_type
              : "%",
          discount_value:
            isData || is_old_variant
              ? collect_items.find((item) => item.uuid == old_variant_uuid)
                  .discount_value
              : "1",
          on_sale_from: new Date(),
          on_sale_to: new Date(),
          date_range:
            isData || is_old_variant
              ? collect_items.find((item) => item.uuid == old_variant_uuid)
                  .date_range
              : this.formatDate(new Date()) +
                " to " +
                this.formatDate(new Date()),
          visibility:
            isData || is_old_variant
              ? collect_items.find((item) => item.uuid == old_variant_uuid)
                  .visibility
              : true,
          uuid:
            isData || is_old_variant
              ? collect_items.find((item) => item.uuid == old_variant_uuid).uuid
              : null,
          is_limited_stock:
            isData || is_old_variant
              ? collect_items.find((item) => item.uuid == old_variant_uuid)
                  .is_limited_stock
              : this.settings.limited_stock,
        };
      });

      if (empty_attributes.attribute_count > 0) {
        notification.warning("Oops, attribute name cannot be empty.");
      }
      if (empty_attributes.variation_count > 0) {
        notification.warning("Oops, variations cannot be empty");
      }
    },
    addAttributeAdOn: function (attribute) {
      this.ad_ons.attributes.push(attribute);
    },
    addVariationAdOn: function (index) {
      this.ad_ons.attributes[index].variations.push({
        action: "create",
        name: "",
      });
    },
    deleteAttributeAdOn: function (index) {
      this.ad_ons.attributes.splice(index, 1);
    },
    deleteVariationAdOn: function (param) {
      this.ad_ons.attributes[param.attribute].variations.splice(
        param.variation,
        1
      );
    },
    generateVariationsAdOn: function () {
      let known_variants = new Set();
      // console.log(this.ad_ons["attributes"]);
      this.ad_ons["attributes"] = this.ad_ons["attributes"]
        .map((product) => {
          product.variations = product.variations.filter(
            (item) =>
              !known_variants.has(item.name.toLowerCase()) &&
              known_variants.add(item.name.toLowerCase()) &&
              item.name != ""
          );
          known_variants = new Set();
          return product;
        })
        .filter((product) => product.name != "");

      this.ad_on_items = this.ad_ons["attributes"].map((product) => {
        product.variations = product.variations.map((variation) => {
          return {
            action: variation.action,
            uuid: null,
            name: variation.name,
            currency: "PHP",
            price: "1",
            sku: null,
            visibility: 1,
            is_free: 0,
          };
        });
        return product;
      });
    },
    setBasicDetailsInit: function () {
      this.basic_details = initialBasicDetails();
    },
    setSimpleProductInit: function () {
      this.simple_product = initialSimpleProduct();
    },
    setProductVariablesInit: function () {
      this.product_variables = initialProductVariables();
    },
    setVariationItemsInit: function () {
      this.variation_items = [];
    },
    setAdOnsInit: function () {
      this.ad_ons = initialAdOns();
    },
    setAdOnItemsInit: function () {
      this.ad_on_items = [];
    },
    setDiscount: function (obj) {
      let data = this.variation_items[obj.index];
      let sale_all = this.product_variables.sale.all;
      let discount = this.product_variables.discount;

      if (sale_all == true && data.on_sale == true) {
        if (data.discount_type == "PHP") {
          if (obj.base == "price") {
            if (Number(data.regular_price) <= Number(discount.discount_value)) {
              data.discount_value = Math.abs(
                Number(data.regular_price - 1) < 0
                  ? 0
                  : Number(data.regular_price - 1)
              ).toString();
            } else {
              data.discount_value = Math.abs(
                Number(discount.discount_value)
              ).toString();
            }
          } else {
            if (Number(data.discount_value) >= Number(data.regular_price)) {
              data.discount_value = Math.abs(
                Number(data.regular_price - 1) < 0
                  ? 0
                  : Number(data.regular_price - 1)
              ).toString();
            }
          }
        }
      } else if (sale_all == false && data.on_sale == true) {
        if (data.discount_type == "PHP") {
          if (obj.base == "price") {
            if (Number(data.regular_price) <= Number(data.discount_value)) {
              data.discount_value = Math.abs(
                Number(data.regular_price - 1) < 0
                  ? 0
                  : Number(data.regular_price - 1)
              ).toString();
            } else {
              data.discount_value = Math.abs(
                Number(data.discount_value)
              ).toString();
            }
          } else {
            if (Number(data.discount_value) >= Number(data.regular_price)) {
              data.discount_value = Math.abs(
                Number(data.regular_price - 1) < 0
                  ? 0
                  : Number(data.regular_price - 1)
              ).toString();
            }
          }
        }
      }

      if (obj.base == "discount_value") {
        if (data.discount_type == "%") {
          if (Number(data.discount_value) >= 99) {
            data.discount_value = Number(99).toString();
          }
        }
      }
    },
    setDiscountValue: function (index) {
      let discount = this.product_variables.discount;
      let variant = this.variation_items[index];

      if (discount.discount_type == "PHP") {
        if (Number(discount.discount_value) >= Number(variant.regular_price)) {
          return Math.abs(
            Number(variant.regular_price - 1) < 0
              ? 0
              : Number(variant.regular_price - 1)
          ).toString();
        } else {
          if (
            Number(variant.regular_price) >= Number(discount.discount_value)
          ) {
            return Math.abs(Number(discount.discount_value)).toString();
          }
        }
      } else {
        return Math.abs(Number(discount.discount_value)).toString();
      }
    },
    inDiscountChecker: function (index) {
      let data = this.variation_items[index];

      if (data.discount_type == "PHP") {
        if (Number(data.discount_value) >= Number(data.regular_price)) {
          data.discount_value = Math.abs(
            Number(data.regular_price - 1) < 0
              ? 0
              : Number(data.regular_price - 1)
          ).toString();
        }
      } else {
        if (Number(data.discount_value) >= 99) {
          data.discount_value = Number(99).toString();
        }
      }
    },
    inSaleChecker: function (index) {
      let data = this.variation_items[index];

      if (data.on_sale == true) {
        this.inDiscountChecker(index);
      }
    },

    getPaymentType: function () {
      this.$store.dispatch("estoreSettings/viewSettings").then(() => {
        this.$store.commit(
          "estoreInventories/setPaymentType",
          this.settings.payment_type
        );
        
        this.simple_product.regular_price =
          this.payment_type == "CC" ? "1" : "0";
      });
    },
  },
  watch: {
    "modal.croppa.status": function (status) {
      if (status === true) {
        this.product_img_upload.croppa.remove();
      }
    },
    "product_variables.sale.all": function (status) {
      this.variation_items = this.variation_items.map((product, index) => {
        let sale = this.product_variables.sale;

        let date_from =
          sale.all == true
            ? sale.date_range.split(" to ")[0]
            : product.on_sale_from;

        let date_to =
          sale.all == true
            ? sale.date_range.split(" to ")[1]
            : product.on_sale_to;

        let discount_value = this.setDiscountValue(index);

        return {
          action: product.action,
          variation: product.variation,
          code: product.code,
          stock: product.stock,
          currency: product.currency,
          regular_price: product.regular_price,
          sku: product.sku,
          on_sale: status,
          discount_type: this.product_variables.discount.discount_type,
          discount_value: discount_value,
          on_sale_from: date_from,
          on_sale_to: date_to,
          date_range: date_from + " to " + date_to,
          visibility: product.visibility,
        };
      });
    },
    "product_variables.sale.date_range": function (date_range) {
      if (this.product_variables.sale.all == true) {
        this.variation_items = this.variation_items.map((product) => {
          return {
            action: product.action,
            variation: product.variation,
            code: product.code,
            stock: product.stock,
            currency: product.currency,
            regular_price: product.regular_price,
            sku: product.sku,
            on_sale: product.on_sale == 1 ? true : false,
            discount_type: product.discount_type,
            discount_value: product.discount_value,
            on_sale_from: product.on_sale_from,
            on_sale_to: product.on_sale_to,
            date_range: date_range,
            visibility: product.visibility,
          };
        });
      }
    },
    "product_variables.discount.discount_type": function (type) {
      if (type == "%") {
        if (this.product_variables.discount.discount_value > 99) {
          this.product_variables.discount.discount_value =
            Number(99).toString();
        }
      }
      if (this.product_variables.sale.all == true) {
        this.variation_items = this.variation_items.map((product, index) => {
          return {
            action: product.action,
            variation: product.variation,
            code: product.code,
            stock: product.stock,
            currency: product.currency,
            regular_price: product.regular_price,
            sku: product.sku,
            on_sale: product.on_sale,
            discount_type: type,
            discount_value:
              product.on_sale == true
                ? this.setDiscountValue(index)
                : product.discount_value,
            on_sale_from: product.on_sale_from,
            on_sale_to: product.on_sale_to,
            date_range: product.date_range,
            visibility: product.visibility,
          };
        });
      }
    },
    "product_variables.discount.discount_value": function () {
      if (this.product_variables.sale.all == true) {
        this.variation_items = this.variation_items.map((product, index) => {
          return {
            action: product.action,
            variation: product.variation,
            code: product.code,
            stock: product.stock,
            currency: product.currency,
            regular_price: product.regular_price,
            sku: product.sku,
            on_sale: product.on_sale,
            discount_type: product.discount_type,
            discount_value:
              product.on_sale == true
                ? this.setDiscountValue(index)
                : product.discount_value,
            on_sale_from: product.on_sale_from,
            on_sale_to: product.on_sale_to,
            date_range: product.date_range,
            visibility: product.visibility,
          };
        });
      }
    },
  },
  mounted() {
    this.$root.$on("bv::modal::hidden", (bvEvent, modalId) => {
      if (bvEvent.target && bvEvent.target.id != "inventoryUploadImages") {
        this.$store.commit("estoreInventories/setModal", {
          type: "new_product",
          status: false,
        });
        this.$store.commit("estoreInventories/setModal", {
          type: "edit_product",
          status: false,
        });
        this.$store.commit("estoreInventories/setModal", {
          type: "delete_product",
          status: false,
        });
      }
    });
  },
};
</script>
